import { AlertTriangle, BellRing, CalendarDays, GripVertical, Layers, StickyNote } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { QuotePdfButton } from "@/components/admin/QuotePdfButton";
import { TONE_CLASSES } from "@/components/admin/leadBoardTokens";
import { formatDate, money } from "@/lib/format";

/* ------------------------------------------------------------------ */
/* card                                                                */
/* ------------------------------------------------------------------ */

/**
 * When the next thing is due, or that it is already late.
 *
 * Overdue wins over the date itself: once a promise has been missed, the date
 * it was made for stops being the useful thing to show.
 */
const ActionBadge = ({ card }) => {
  if (card.next_action_overdue) {
    return (
      <Badge
        variant="outline"
        data-testid="lead-action-overdue-badge"
        className="gap-1 border-destructive/30 bg-destructive/10 px-1.5 py-0 text-[10px] font-semibold text-destructive"
      >
        <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" />
        Action overdue
      </Badge>
    );
  }
  if (card.next_action_date) {
    return (
      <Badge
        variant="outline"
        data-testid="lead-next-action-badge"
        className={`gap-1 px-1.5 py-0 text-[10px] font-semibold ${TONE_CLASSES.teal}`}
      >
        <CalendarDays className="h-2.5 w-2.5" aria-hidden="true" />
        {formatDate(card.next_action_date)}
      </Badge>
    );
  }
  return null;
};

/** Why a closed lead closed - or a nudge to say, if nobody has. */
const ReasonBadge = ({ card }) => {
  if (card.outcome_reason_label) {
    return (
      <Badge
        variant="outline"
        data-testid="lead-outcome-reason-badge"
        className={`px-1.5 py-0 text-[10px] font-semibold ${
          card.status === "won" ? TONE_CLASSES.success : TONE_CLASSES.muted
        }`}
      >
        {card.outcome_reason_label}
      </Badge>
    );
  }
  if (card.needs_outcome_reason) {
    return (
      <Badge
        variant="outline"
        data-testid="lead-needs-reason-badge"
        className="gap-1 border-dashed border-muted-foreground/40 px-1.5 py-0 text-[10px] font-normal text-muted-foreground"
      >
        Add a reason
      </Badge>
    );
  }
  return null;
};

/**
 * One quote on the board.
 *
 * The card carries its own stage selector and PDF link so the two things the
 * team does most often - move a lead on, and pull up the quote that was sent -
 * take one press, without dragging and without opening the panel first. Those
 * controls stop their click from bubbling, so pressing them never opens the
 * detail sheet by accident.
 *
 * When somebody has quoted more than once, the card says which of their quotes
 * it is and when it was issued. Without that, six cards reading
 * "Sipho Ndlovu · Ndlovu Logistics" are impossible to tell apart.
 */
export const LeadCard = ({ card, stages, onOpen, onStageChange, onDragStart, dragging }) => {
  const stop = (event) => event.stopPropagation();

  return (
    <article
      data-testid="lead-card"
      data-quote-ref={card.quote_ref}
      draggable
      onDragStart={(event) => onDragStart(event, card)}
      className={`group cursor-pointer rounded-lg border bg-white p-3 text-left outline-none transition-[border-color,box-shadow,opacity] duration-150 hover:border-[color:var(--tt-teal-line)] hover:shadow-[0_6px_20px_rgba(21,32,38,0.08)] ${
        card.gone_quiet ? "border-[color:var(--tt-warn-border)]" : "border-border"
      } ${dragging ? "opacity-40" : ""}`}
      onClick={() => onOpen(card)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <button
            type="button"
            data-testid="lead-card-open-button"
            onClick={(event) => {
              stop(event);
              onOpen(card);
            }}
            className="max-w-full truncate rounded text-left text-sm font-bold text-foreground outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-primary/30"
          >
            {card.full_name}
          </button>
          <p className="truncate text-xs text-muted-foreground">{card.company}</p>
        </div>
        <GripVertical
          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground"
          aria-hidden="true"
        />
      </div>

      {card.is_repeat ? (
        <Badge
          variant="outline"
          data-testid="lead-quote-sequence-badge"
          className={`mt-2 gap-1 px-1.5 py-0 text-[10px] font-semibold ${TONE_CLASSES.teal}`}
        >
          <Layers className="h-2.5 w-2.5" aria-hidden="true" />
          Quote {card.quote_number} of {card.quote_count}
        </Badge>
      ) : null}

      <div className="mt-2.5">
        <span className="font-amount text-base font-bold text-primary">
          {money(card.quote_total)}
          {card.pricing_type === "monthly" ? (
            <span className="text-[11px] font-normal text-muted-foreground">/month</span>
          ) : null}
        </span>
      </div>

      <p className="mt-1.5 truncate text-[11px] text-muted-foreground">{card.service_label}</p>

      {/* Date and reference share a line and never wrap: a reference broken
          across two lines is the thing that makes repeat quotes unreadable. */}
      <p className="mt-1 flex items-center gap-1.5 overflow-hidden text-[10px] text-muted-foreground">
        <CalendarDays className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
        <span data-testid="lead-card-quoted-on" className="shrink-0 whitespace-nowrap">
          Quoted {formatDate(card.created_at)}
        </span>
        <span aria-hidden="true" className="shrink-0">
          ·
        </span>
        <span
          data-testid="lead-card-reference"
          className="truncate whitespace-nowrap font-amount"
        >
          {card.quote_ref}
        </span>
      </p>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {card.gone_quiet ? (
          <Badge
            variant="outline"
            data-testid="lead-gone-quiet-badge"
            className={`gap-1 px-1.5 py-0 text-[10px] font-semibold ${TONE_CLASSES.amber}`}
          >
            <BellRing className="h-2.5 w-2.5" aria-hidden="true" />
            Gone quiet · {card.days_since_activity}d
          </Badge>
        ) : null}
        <ActionBadge card={card} />
        {card.notes_count ? (
          <Badge
            variant="outline"
            className="gap-1 border-border px-1.5 py-0 text-[10px] font-normal text-muted-foreground"
          >
            <StickyNote className="h-2.5 w-2.5" aria-hidden="true" />
            {card.notes_count}
          </Badge>
        ) : null}
        <ReasonBadge card={card} />
      </div>

      {/* Press-to-update, so the board never depends on dragging. */}
      <div
        className="mt-3 flex items-center gap-1.5 border-t border-border pt-2.5"
        onClick={stop}
        onKeyDown={stop}
        role="presentation"
      >
        <Select value={card.status} onValueChange={(next) => onStageChange(card, next)}>
          <SelectTrigger
            data-testid="lead-card-stage-select"
            aria-label={`Follow-up stage for ${card.full_name}, quote ${card.quote_ref}`}
            className="h-9 flex-1 text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stages.map((stage) => (
              <SelectItem key={stage.key} value={stage.key} className="text-xs">
                {stage.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <QuotePdfButton quoteRef={card.quote_ref} compact />
      </div>
    </article>
  );
};
